// ---------------------------------------------------------------------------
// validation.ts — Email and phone validation helpers for German signup forms
// No external dependencies.
// ---------------------------------------------------------------------------

import { i18n } from "@/i18n";

// ---------------------------------------------------------------------------
// Email
// ---------------------------------------------------------------------------

/**
 * RFC-5321-inspired email regex. Rejects:
 *   - missing @ or domain
 *   - double dots anywhere in local or domain
 *   - leading / trailing dots in local part or domain labels
 *   - spaces
 *
 * Accepts standard addresses like user@firma.de, name.name@sub.domain.com
 */
const EMAIL_REGEX =
  /^[a-zA-Z0-9](?:[a-zA-Z0-9._%+-]*[a-zA-Z0-9])?@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

/** Returns a localised error string, or null when the email is valid. */
export function validateEmail(email: string): string | null {
  const trimmed = email.trim();

  if (!trimmed) {
    return i18n.t("validation.emailInvalid", { ns: "search" });
  }

  // Reject double dots
  if (trimmed.includes("..")) {
    return i18n.t("validation.emailInvalid", { ns: "search" });
  }

  // Reject spaces (trim already caught leading/trailing; check internal)
  if (/\s/.test(trimmed)) {
    return i18n.t("validation.emailInvalid", { ns: "search" });
  }

  if (!EMAIL_REGEX.test(trimmed)) {
    return i18n.t("validation.emailInvalid", { ns: "search" });
  }

  return null;
}

// ---------------------------------------------------------------------------
// Phone
// ---------------------------------------------------------------------------

/** Strips every character that is not a decimal digit (0-9). */
export function extractDigits(phone: string): string {
  return phone.replace(/\D/g, "");
}

/**
 * Validates a phone number after stripping non-digits.
 * Accepts 6-15 digits to cover German landlines (short area codes),
 * mobile numbers, and international formats.
 *
 * Returns a localised error string, or null when the number is valid.
 */
export function validatePhone(phone: string): string | null {
  const digits = extractDigits(phone);

  if (digits.length < 6 || digits.length > 15) {
    return i18n.t("validation.phoneInvalid", { ns: "search" });
  }

  return null;
}

// ---------------------------------------------------------------------------
// Website URL
// ---------------------------------------------------------------------------

/**
 * Normalizes a website URL: ensures https:// prefix.
 * Accepts: "example.de", "www.example.de", "http://example.de", "https://example.de"
 */
export function normalizeWebsiteUrl(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

/**
 * Validates a website input (before normalization).
 * Must contain at least one dot and no spaces.
 * Returns localised error string or null.
 */
export function validateWebsite(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null; // empty is OK (optional field)
  if (/\s/.test(trimmed))
    return i18n.t("validation.websiteNoSpaces", { ns: "search" });
  if (!trimmed.includes("."))
    return i18n.t("validation.websiteInvalid", { ns: "search" });
  return null;
}

// ---------------------------------------------------------------------------
// Country data
// ---------------------------------------------------------------------------

export interface Country {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
}

/**
 * 15 countries relevant for a DACH-centric B2B product.
 * Germany is first; the remainder are sorted alphabetically by name.
 * Country names are hardcoded proper nouns (ISO display names for the
 * PhoneInput dropdown). Full locale-aware resolution is deferred.
 */
export const COUNTRIES: Country[] = [
  // Default first entry
  { code: "DE", name: "Deutschland", dialCode: "+49", flag: "🇩🇪" },
  // Alphabetical by name
  { code: "BE", name: "Belgien", dialCode: "+32", flag: "🇧🇪" },
  { code: "DK", name: "Dänemark", dialCode: "+45", flag: "🇩🇰" },
  { code: "FR", name: "Frankreich", dialCode: "+33", flag: "🇫🇷" },
  { code: "GB", name: "Großbritannien", dialCode: "+44", flag: "🇬🇧" },
  { code: "IT", name: "Italien", dialCode: "+39", flag: "🇮🇹" },
  { code: "LU", name: "Luxemburg", dialCode: "+352", flag: "🇱🇺" },
  { code: "NL", name: "Niederlande", dialCode: "+31", flag: "🇳🇱" },
  { code: "AT", name: "Österreich", dialCode: "+43", flag: "🇦🇹" },
  { code: "PL", name: "Polen", dialCode: "+48", flag: "🇵🇱" },
  { code: "SE", name: "Schweden", dialCode: "+46", flag: "🇸🇪" },
  { code: "CH", name: "Schweiz", dialCode: "+41", flag: "🇨🇭" },
  { code: "ES", name: "Spanien", dialCode: "+34", flag: "🇪🇸" },
  { code: "CZ", name: "Tschechien", dialCode: "+420", flag: "🇨🇿" },
  { code: "US", name: "USA", dialCode: "+1", flag: "🇺🇸" },
];
